/**
 * Created by Carsten Bühler
 * User: CBuehler on 07.04.2016
 * Copyright: xdot GmbH 2015
 */

'use strict';

import { DataUtil } from 'plugin/liferay-widgets-react-web';
import uniqueId, { METRIC_TYPE } from '../../utils/timeseries-util';

export default class HCTimeSeriesPlugin {

    initOptions(o) {
        return this.options(o);
    }

    options(o) {
        o = {
            title: {
                text: ' '
            },
            chart: {
                animation: false,
                alignTicks: true,
                zoomType: 'xy',
                resetZoomButton: {
                    position: {
                        align: 'left',
                        x: 5,
                        y: 5,
                    },
                },
            },
            credits: {
                text: '',
                href: '',
                style: { 'fontSize': '12px' }
            },
            tooltip: {
                xDateFormat: '%d.%m.%Y %H:%M:%S',
                valueDecimals: 2,
                shared: true
            },
            plotOptions: {
                series: {
                    animation: false
                },
                scatter: {
                    marker: {
                        radius: 2
                    }
                }
            },
            rangeSelector: {
                selected: 1,
                inputEditDateFormat: '%d-%m-%Y',
                inputDateFormat: '%d %b %Y',
                buttons: [ {
                    type: 'month',
                    count: 1,
                    text: '1M'
                }, {
                    type: 'week',
                    count: 1,
                    text: '1W'
                }, {
                    type: 'day',
                    count: 3,
                    text: '3T'
                }, {
                    type: 'day',
                    count: 1,
                    text: '1T'
                }, {
                    type: 'hour',
                    count: 12,
                    text: '12H'
                }, {
                    type: 'all',
                    text: 'Alle'
                } ]
            },
            legend: {
                enabled: false
            },
            mapNavigation: {
                enabled: true,
                enableButtons: false
            },
            xAxis: {
                type: 'datetime',
                dateTimeLabelFormats: { // don't display the dummy year
                    month: '%e. %b',
                    year: '%b'
                },
                title: {
                    text: 'Datum'
                },
                tickInterval: 24 * 3600 * 1000, // 1 day
                minorTickInterval: 1 * 3600 * 1000, // 1 hour
                minRange: 3 * 3600 * 1000, // 3 hours
                minorTickLength: 5,
                minorTickWidth: 1,
                minorGridLineWidth: 0
            },
            yAxis: {
                min: 0,
                floor: -1.0,
                startOnTick: false,
                lineWidth: 1,
                opposite: false,
                title: {
                    useHTML: true
                },
            },
            lang: {
                noData: 'Es liegen keine aktuellen Daten vor'
            },
            noData: {
                style: {
                    fontWeight: 'bold',
                    color: '#999999'
                }
            },
            ...o
        };
        return o;
    }

    init(chart, o) {
        this.chart = chart;
    }

    update(chart, o) {
        const { item } = o;
        const series = DataUtil.asArray(item);
        if (this.lastSeries != item && series && series.length > 0) {
            this.updateSeries(chart, series);
        } else if (series && series.length === 0) {
            chart.series.forEach(s => s.remove());
            chart.redraw();
        }

        this.lastSeries = item;
    }

    updateSeries(chart, seriesArray) {
        seriesArray = seriesArray.filter(series => series.times.length > 0);

        if (seriesArray.length == 0) {
            return;
        }

        const { referenceValueSeriesArray, updates } = this.preprocessSeriesArray(chart, seriesArray);
        let toRemove = this.toRemove(chart, updates);

        if (toRemove) {
            toRemove.forEach(id => {
                chart.get(id).remove(false); // no redraw here
            });
            chart.redraw();
        }

        this.plotSeries(chart, updates);
        this.updateAxis(chart);
        this.updateGrid(referenceValueSeriesArray);
        this.updateCredits(chart);
    }

    preprocessSeriesArray(chart, seriesArray) {
        const referenceValueSeriesArray = seriesArray.filter(series => series.type === METRIC_TYPE.REFERENCE_VALUES);
        if (referenceValueSeriesArray.length > 0) {
            seriesArray = seriesArray.filter(series => series.type !== METRIC_TYPE.REFERENCE_VALUES);
        }

        const updates = seriesArray.map(series => {
            const seriesId = uniqueId(series);
            const updateType = this.checkUpdateType(chart, seriesId, series);
            return {
                seriesId,
                updateType,
                series
            };
        });

        return { referenceValueSeriesArray, updates };
    }

    toRemove(chart, updates) {
        let toRemove = null;
        chart.series.forEach(data => {
            let found = false;
            updates.forEach(update => {
                if (data.options && data.options.id == update.seriesId) {
                    found = true;
                }
            });
            if (!found) {
                toRemove = toRemove || [];
                toRemove.push(data.options.id);
            }
        });
        return toRemove;
    }

    plotSeries(chart, data, o) {
        if (!data) return;

        data.forEach(s => {
            const { series, seriesId, updateType } = s;
            const { times, values } = series;
            const newData = times.map((t, i) => [ Number.parseInt(t), values[i] ]);
            const name = this.getName(series, false);
            const type = this.getTypeOfChart(series);

            const unit = ' ' + series.labels.unit;
            const axisTitle = `${this.getClassName(series)} [${unit}]`;
            if (chart.yAxis[0] && (chart.yAxis[0].options.title.text !== axisTitle)) {
                chart.yAxis[0].setTitle({ text: axisTitle });
            }

            let seriesOptions = {
                id: seriesId,
                name: name,
                data: newData,
                color: this.getColor(series),
                type: type === 'line' ? 'spline' : type,
                ...o
            };

            seriesOptions.tooltip = {
                ...seriesOptions.tooltip,
                headerFormat: '<span style="font-size: 10px">{point.key}</span><br/>',
                pointFormat: '<span style="color:{point.color}">\u25CF</span> {series.name}: <b>{point.y}' + unit + '</b><br/>'
            };

            this.doUpdate(chart, seriesId, newData, seriesOptions, updateType);
        });

    }

    doUpdate(chart, seriesId, newData, options, updateType) {
        const chartSeries = chart.get(seriesId);
        if (chartSeries) {
            chartSeries.setData(newData);
        } else if (!chartSeries && updateType === -1) {
            chart.addSeries(options);
        }
    }

    updateCredits(chart) {
        const
            xAxis = chart.xAxis[0],
            oldCredits = chart.credits,
            extremes = xAxis.getExtremes(),
            { dataMax, dataMin } = extremes;

        const text = 'Zeitbereich vom ' +
            new Date(dataMin).toLocaleDateString() +
            ' bis ' +
            new Date(dataMax).toLocaleDateString() +
            ' ROHDATEN ohne Gewähr';

        if (text !== oldCredits.textStr) {
            chart.credits.update({
                text: text
            });
        }
    }

    updateAxis(chart) {
        const
            yAxis = chart.yAxis[0],
            extremes = yAxis.getExtremes(),
            max = Math.ceil(extremes.dataMax),
            min = extremes.dataMin;

        if (max !== yAxis.max) {
            yAxis.update({
                max: max + 1,
                min: min
            });
        }
    }

    //See: https://inovum-dev.de/jira/browse/FLIWAS-788
    updateGrid(referenceValueSeriesArray/*, yAxisRange*/) {

        const yAxis = this.chart.yAxis[0],
            plotLinesAndBands = this.chart.yAxis[0].plotLinesAndBands;

        referenceValueSeriesArray.forEach(rvS => {
            const value = rvS.values[rvS.values.length - 1],
                title = rvS.meta.title,
                color = rvS.meta.color || '#000000';

            const o = {
                id: title,
                label: {
                    align: 'right',
                    text: title,
                    style: {
                        color: 'black',
                        padding: 2
                    }
                },
                value: value,
                color: color,
                width: 1,
                zIndex: 3
            };

            const idx = plotLinesAndBands.findIndex(l => l.id === title);

            if (idx === -1) {
                yAxis.addPlotLine(o);
            } else {
                const plotLine = plotLinesAndBands[idx],
                    { options } = plotLinesAndBands[idx];
                if (options.label.text !== title || options.value !== value || options.color !== color) {
                    plotLine.update(o);
                }
            }

        });
    }

    /**
     *  Check type of the series update, returns:
     *      -1  a 'full' update needed
     *      0  no update data is equal
     *      n number of values that needs to be appended
     */
    checkUpdateType(chart, seriesId, newSeries) {
        let series = chart.get(seriesId),
            newLen = newSeries.values.length;

        const data = series && series.options && series.options.data;

        if (!data || newLen < data.length) {
            return -1;
        }

        if (this.endsWith(data, newSeries)) {

            if (data.length == newLen) {
                return 0;
            }

            if (data[data.length - 1][0] > newSeries.times[newSeries.times.length - 1]) {
                console.error('Invalid update found! ', data, newSeries);
                return -1;
            }

            return newLen - data.length;
        }

        return -1;
    }

    endsWith(cvalues, { values, times }) {
        if (cvalues.length > values.length) {
            return false;
        }

        for (let i = cvalues.length - 1, v, t ; i >= 0 ; i--) {
            t = cvalues[i][0];
            v = cvalues[i][1];
            if (v !== values[i] || t !== times[i]) {
                return false;
            }
        }
        return true;
    }

    getTypeOfChart(series) {
        switch (series.type) {
            case METRIC_TYPE.DATA_POINTS:
                return 'scatter';
            case METRIC_TYPE.SERIES:
                return 'line';
            default:
                console.warn('Unknown series type: ' + series.type);
                return 'line';
        }
    }

    getName(series) {
        return series.metric;
    }

    getColor(series) {

    }

    //TODO CURRENTLY UNUSED
    getRegionFromSet(seriesArray) {

    }

}




// WEBPACK FOOTER //
// ./js/charts/plugins/HCTimeSeriesPlugin.js